import { useCallback, useEffect, useState } from 'react';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { addJoinRequest, markJoinRequestAnswering, pruneJoinRequests, removeJoinRequest } from './groupJoinRequests';
/**
 * The join requests the host has to answer, newest last, and how to answer
 * one (groupJoinRequests.ts). Each request leaves the list when the server
 * closes it, when its answer window passes, or when the server refuses the
 * answer (the request already ended).
 *
 * @returns {{ requests: GroupJoinRequest[], respond: Function }}
 */
export function useGroupJoinRequests() {
    const [requests, setRequests] = useState([]);
    useEffect(() => {
        const onRequest = (data) => setRequests(previous => addJoinRequest(previous, data, Date.now()));
        const onResolved = (data) => setRequests(previous => removeJoinRequest(previous, data?.requestId));
        const onError = (data) => {
            if (data?.operation === 'group_join_respond') {
                setRequests(previous => removeJoinRequest(previous, data.requestId));
            }
        };
        queueService.on('groupJoinRequest', onRequest);
        queueService.on('groupJoinResolved', onResolved);
        queueService.on('error', onError);
        return () => {
            queueService.off('groupJoinRequest', onRequest);
            queueService.off('groupJoinResolved', onResolved);
            queueService.off('error', onError);
        };
    }, []);
    // A request nobody closed (a lost message) goes when its window passes.
    useEffect(() => {
        if (!requests.length) {
            return undefined;
        }
        const next = Math.min(...requests.map(entry => entry.expiresAt));
        const timer = setTimeout(() => setRequests(previous => pruneJoinRequests(previous, Date.now())), Math.max(0, next - Date.now()) + 50);
        return () => clearTimeout(timer);
    }, [requests]);
    const respond = useCallback((request, accept) => {
        setRequests(previous => markJoinRequestAnswering(previous, request.requestId));
        queueService.respondToGroupJoin(request.callId, request.requestId, accept);
    }, []);
    return { requests, respond };
}
